import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { stagePublicAPI } from '../services/api';
import ChampDate from './ChampDate';
import './StagePublic.css';
import StageDepotPublic from './StageDepotPublic';
import StageCertifDepotPublic from './StageCertifDepotPublic';
import StageCertifMaitrePublic from './StageCertifMaitrePublic';
import StageEvaluationPublique from './StageEvaluationPublique';
import StageVisitePublique from './StageVisitePublique';

/**
 * SUIVI DE STAGE — la page publique de la fiche de liaison (lots 3.2 et 3.2 bis)
 * =============================================================================
 * Spec     : doc/modules/suivi_de_stage.md § Formulaires publics,
 *            écarts E24 (deux portes étudiant), E55 (un seul composant), E56
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#tab-formulaire)
 *
 * ⚠️ CETTE PAGE N'EST PAS DANS L'APPLICATION. Elle s'ouvre **sans compte**, sans
 * sidebar et sans `AppLayout` : ceux qui la voient sont des étudiants et des
 * maîtres de stage. Ils n'ont pas de session et n'en auront pas.
 *
 * ── TROIS PORTES, UN SEUL FORMULAIRE ───────────────────────────────────────
 *   · `/stage/fiche-liaison/:code` — le lien **commun**, envoyé à toute la
 *     promotion ou posé sur Moodle. Un écran d'identification à quatre champs
 *     précède le formulaire (écart E24) ;
 *   · `/stage/etudiant/:token` — le lien **tokenisé** des relances. Le jeton dit
 *     déjà de quel dossier il s'agit ; l'étudiant prouve qu'il est le bon avec
 *     son **numéro et sa date de naissance** — que la page ne connaît PAS (E79) ;
 *   · `/stage/fiche/:token` — le **maître de stage**, étape ④. Le formulaire
 *     arrive **pré-rempli de ce que l'étudiant a saisi**, tout est corrigeable,
 *     et s'y ajoutent les champs qui lui sont propres.
 *
 * ⚠️ AUCUN NUMÉRO ÉTUDIANT N'EST DEMANDÉ AU MAÎTRE (écart E56). La maquette en
 * dessine un — c'est un reste de la page étudiant. Un maître de stage ne connaît
 * pas ce numéro : le lui demander arrêterait au seuil la seule personne qu'on a
 * mis trois étapes à atteindre. Sa porte, c'est son jeton.
 *
 * ⚠️ Un seul composant, un seul formulaire (écart E55). Deux composants auraient
 * divergé — et c'est toujours celui qu'on ouvre le moins qui pourrit.
 *
 * ── CE QUE CETTE PAGE NE FAIT PAS ──────────────────────────────────────────
 * Elle n'affiche **aucune donnée personnelle avant identification** : l'écran
 * d'accueil du lien commun ne montre que la formation, le semestre et les
 * dates. Ni nom, ni effectif — sinon le lien deviendrait un annuaire.
 */

const CHAMPS_ENTREPRISE = [
    { cle: 'entreprise_nom',     label: "Nom de l'entreprise", large: true, placeholder: 'Ex : Innovate SAS' },
    { cle: 'entreprise_adresse', label: 'Adresse',             large: true, placeholder: 'Adresse complète' },
    { cle: 'entreprise_cp',      label: 'Code postal',         placeholder: '91000' },
    { cle: 'entreprise_ville',   label: 'Ville',               placeholder: 'Évry' }
];

const CHAMPS_MAITRE = [
    { cle: 'maitre_nom',       label: 'Nom',            placeholder: 'Nom' },
    { cle: 'maitre_prenom',    label: 'Prénom',         placeholder: 'Prénom' },
    { cle: 'maitre_fonction',  label: 'Fonction / Poste', large: true, placeholder: 'Ex : Responsable Qualité' },
    { cle: 'maitre_email',     label: 'Email',          type: 'email', placeholder: 'email@entreprise.fr' },
    { cle: 'maitre_telephone', label: 'Téléphone',      type: 'tel',   placeholder: '06 XX XX XX XX' }
];

const dateFr = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};

/**
 * ⚠️ LE CODE DE LA SECTION QUE L'ÉCRAN SORT DU LOT (écart E54).
 *
 * La case « convention université » n'est pas une section libre comme les
 * autres : le tableau de bord a une colonne 🏛️ qui en dépend, et l'écran lui
 * donne sa propre place. Elle se reconnaît à son `code`, jamais à son titre —
 * un titre est une chaîne que quelqu'un peut réécrire au lot 3.8, et la
 * reconnaissance échouerait sans la moindre erreur.
 */
const CODE_CONVENTION = 'convention_universite';

/** Un message plein écran — accueil, refus, succès. */
function Message({ ton = 'info', icone, titre, children }) {
    return (
        <div className="pub-page">
            <div className={'pub-message pub-message-' + ton}>
                <i className={'fa-solid ' + (icone || 'fa-circle-info')} />
                <h2>{titre}</h2>
                <div>{children}</div>
            </div>
        </div>
    );
}

function Champ({ def, valeur, onChange, disabled }) {
    return (
        <div className={'form-group' + (def.large ? ' form-group-large' : '')}>
            <label htmlFor={'f-' + def.cle}>
                {def.label} <span className="form-required">*</span>
            </label>
            <input
                id={'f-' + def.cle}
                type={def.type || 'text'}
                value={valeur || ''}
                placeholder={def.placeholder}
                disabled={disabled}
                onChange={(e) => onChange(def.cle, e.target.value)}
            />
        </div>
    );
}

/**
 * La civilité du maître (v82, décision A du 2026-10-17) : FACULTATIVE, jamais devinée par le prénom. L'étudiant
 * peut répondre « Je ne sais pas » ; le maître voit le choix de l'étudiant et le corrige (« Non précisée »).
 * « Rien » s'envoie quand même (chaîne vide) : c'est un choix, il efface une civilité erronée.
 */
function ChoixCivilite({ valeur, onChange, disabled, pourMaitre }) {
    const options = [['Madame', 'Madame'], ['Monsieur', 'Monsieur'], ['', pourMaitre ? 'Non précisée' : 'Je ne sais pas']];
    return (
        <div className="form-group form-group-large">
            <label>Civilité <span className="pub-discret">(facultatif)</span></label>
            <div className="pub-civilite" role="radiogroup" aria-label="Civilité du maître de stage">
                {options.map(([v, lib]) => (
                    <label key={lib} className={(valeur || '') === v ? 'on' : ''}>
                        <input type="radio" name="f-maitre_civilite" checked={(valeur || '') === v} disabled={disabled}
                               onChange={() => onChange('maitre_civilite', v)} /> {lib}
                    </label>
                ))}
                <span className="pub-aide">
                    {pourMaitre ? "Saisie par l'étudiant — corrigez-la si besoin."
                                : 'Elle sert aux formules de politesse des courriels envoyés à votre maître.'}
                </span>
            </div>
        </div>
    );
}

/** Une section libre du modèle — le type vient du paramétrage. */
function SectionLibre({ section, valeur, onChange, disabled }) {
    const options = Array.isArray(section.options) ? section.options : [];
    const commun = {
        id: 'sec-' + section.id,
        value: valeur || '',
        disabled,
        onChange: (e) => onChange(section.id, e.target.value)
    };

    return (
        <div className="form-group form-group-large">
            <label htmlFor={commun.id}>
                {section.titre}
                {section.obligatoire && <span className="form-required"> *</span>}
            </label>

            {section.type_champ === 'textarea' && <textarea {...commun} placeholder={section.placeholder} />}
            {section.type_champ === 'text' && <input type="text" {...commun} placeholder={section.placeholder} />}
            {section.type_champ === 'date' && <ChampDate {...commun} />}
            {section.type_champ === 'select' && (
                <select {...commun}>
                    <option value="">— Choisir —</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            )}
            {section.type_champ === 'radio' && (
                <div className="pub-choix">
                    {options.map((o) => (
                        <label key={o}>
                            <input type="radio" name={commun.id} value={o} disabled={disabled}
                                   checked={valeur === o} onChange={() => onChange(section.id, o)} />
                            {o}
                        </label>
                    ))}
                </div>
            )}
            {section.type_champ === 'checkbox' && (
                <div className="pub-choix">
                    {/* Plusieurs cases : la valeur est la liste des cochées,
                        séparée par des virgules — c'est ce que la base attend. */}
                    {(options.length ? options : ['Oui']).map((o) => {
                        const cochees = String(valeur || '').split(',').map((x) => x.trim()).filter(Boolean);
                        return (
                            <label key={o}>
                                <input
                                    type="checkbox" disabled={disabled}
                                    checked={cochees.includes(o)}
                                    onChange={(e) => {
                                        const suite = e.target.checked
                                            ? [...cochees, o]
                                            : cochees.filter((x) => x !== o);
                                        onChange(section.id, suite.join(', '));
                                    }}
                                />
                                {o}
                            </label>
                        );
                    })}
                </div>
            )}
            {section.placeholder && section.type_champ !== 'textarea' && section.type_champ !== 'text' && (
                <span className="pub-aide">{section.placeholder}</span>
            )}
        </div>
    );
}

/**
 * ⚠️ LOT 3.8c — L'APERÇU DE L'ÉDITEUR DE MODÈLES, C'EST CETTE PAGE (E116).
 * `apercu` = `{ vue: 'etudiant' | 'maitre', contenu }` : un contenu FICTIF, déjà
 * filtré sur qui remplit. Pas d'identification, aucun appel au serveur, et la
 * validation ne part pas.
 */
function StageFicheLiaisonPublique({ porte: portePrevueProp, apercu = null }) {
    const { code, token } = useParams();
    // Aperçu : la porte du maître, ou une porte étudiant SANS contrôle d'identité.
    const portePrevue = apercu ? (apercu.vue === 'maitre' ? 'maitre' : 'apercu') : portePrevueProp;

    /**
     * ⚠️ SUR L'ADRESSE COURTE, C'EST LE SERVEUR QUI DIT LA PORTE (écart E66).
     *
     * `/s/<jeton>` ne dit pas s'il ouvre la fiche de l'étudiant ou celle du
     * maître de stage : **le jeton le sait**, l'adresse non. L'écran attend donc
     * la réponse avant de savoir quoi dessiner — et c'est ce qui fait qu'il n'y
     * a plus de « mauvaise page » où un jeton puisse se tromper.
     */
    const [porteResolue, setPorteResolue] = useState(null);
    const porte = portePrevue === 'auto' ? porteResolue : portePrevue;
    // Lot 3.4 bis : l'en-tête du portail de dépôt, quand le jeton en est un.
    const [enteteDepot, setEnteteDepot] = useState(null);
    // Lot 3.6 : la grille d'évaluation, quand le jeton est celui du maître pour l'étape ⑨.
    const [pageEval, setPageEval] = useState(null);

    const [chargement, setChargement] = useState(true);
    const [erreurPage, setErreurPage] = useState(null);
    const [campagne, setCampagne] = useState(null);
    const [contenu, setContenu] = useState(null);     // dossier + modele + sections + reponses
    const [identite, setIdentite] = useState({ numero: '', nom: '', prenom: '', dateNaissance: '' });
    const [erreurIdent, setErreurIdent] = useState(null);
    const [valeurs, setValeurs] = useState({});
    const [sections, setSections] = useState({});
    const [erreurForm, setErreurForm] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const [envoye, setEnvoye] = useState(null);

    /** Pose le formulaire à partir de ce que le serveur a renvoyé. */
    const poserContenu = useCallback((data) => {
        setContenu(data);

        /**
         * ⚠️ « SAUF CONTRE-ORDRE » VEUT DIRE COCHÉE PAR DÉFAUT.
         *
         * La phrase de la spec est *« Sauf contre-ordre, la convention sera
         * celle de l'université »*, et la spec l'écrit avec un **☑**. Une case
         * décochée dirait donc exactement le contraire de son propre libellé —
         * et le maître de stage qui la laisse telle quelle, croyant approuver,
         * aurait refusé la convention de l'université sans le savoir.
         *
         * ⚠️ LE DÉFAUT NE S'APPLIQUE QUE SI LA QUESTION N'A JAMAIS ÉTÉ POSÉE.
         * Si la réponse existe déjà — même vide, c'est-à-dire « décochée sciemment »
         * — on la respecte. Recocher une case que quelqu'un a décochée serait
         * la pire des deux erreurs.
         */
        const sectionsPosees = { ...(data.reponses?.sections || {}) };
        const conv = (data.sections || []).find((s) => s.code === CODE_CONVENTION);
        if (conv && conv.type_champ === 'checkbox' && !(conv.id in sectionsPosees)) sectionsPosees[conv.id] = 'Oui';

        const f = data.reponses?.fiche || {};
        setValeurs({
            entreprise_nom: f.entreprise_nom || '', entreprise_adresse: f.entreprise_adresse || '',
            entreprise_cp: f.entreprise_cp || '', entreprise_ville: f.entreprise_ville || '',
            maitre_civilite: f.maitre_civilite || '',
            maitre_nom: f.maitre_nom || '', maitre_prenom: f.maitre_prenom || '',
            maitre_fonction: f.maitre_fonction || '', maitre_email: f.maitre_email || '',
            maitre_telephone: f.maitre_telephone || '', missions: f.missions || ''
        });
        setSections(sectionsPosees);
    }, []);

    // ── Ouverture ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (apercu) { poserContenu(apercu.contenu); setChargement(false); return undefined; }
        let annule = false;
        setChargement(true);
        setErreurPage(null);

        // ⚠️ Seule la porte COMMUNE commence par un écran d'identification : les
        // deux portes tokenisées ouvrent directement le formulaire, parce que le
        // jeton a déjà dit de quel dossier il s'agit.
        const appel = portePrevue === 'auto' ? stagePublicAPI.ouvrirLien(token)
                    : portePrevue === 'maitre' ? stagePublicAPI.ouvrirMaitre(token)
                    : portePrevue === 'tokenisee' ? stagePublicAPI.ouvrirTokenise(token)
                    : stagePublicAPI.lireCampagne(code);

        appel
            .then((r) => {
                if (annule) return;
                // ⚠️ La porte AVANT le contenu : c'est elle qui décide de ce que
                // le formulaire affiche et de l'adresse où il soumettra.
                // ⚠️ Un jeton de DÉPÔT ouvre le portail, pas la fiche (lot 3.4 bis) :
                // c'est le serveur qui le dit, l'écran ne devine rien (E66).
                if (r.data.porte === 'depot') { setPorteResolue('depot'); setEnteteDepot(r.data); return; }
                // Lot C.2 : un jeton de dépôt de CERTIFICATION ouvre sa propre page (bleu encre).
                if (r.data.porte === 'certif_depot') { setPorteResolue('certif_depot'); setEnteteDepot(r.data); return; }
                // Lot C.3 : un jeton de MAÎTRE (certification) ouvre sa page — la page complète, sans identification.
                if (r.data.porte === 'certif_attestation') { setPorteResolue('certif_attestation'); setEnteteDepot(r.data); return; }
                if (r.data.porte === 'evaluation') { setPorteResolue('evaluation'); setPageEval(r.data); return; }
                // Lot 3.5c : la fiche de visite du tuteur (attente avant le 1er jour du stage, puis la fiche).
                if (r.data.porte === 'visite') { setPorteResolue('visite'); setPageEval(r.data); return; }
                if (portePrevue === 'auto') setPorteResolue(r.data.porte);
                if (portePrevue === 'commune') setCampagne(r.data.campagne);
                else poserContenu(r.data);
            })
            .catch((e) => {
                if (annule) return;
                setErreurPage({
                    titre: e.response?.data?.error || 'Page indisponible',
                    detail: e.response?.data?.detail || e.message,
                    etat: e.response?.data?.etat
                });
            })
            .finally(() => { if (!annule) setChargement(false); });

        return () => { annule = true; };
    }, [portePrevue, code, token, poserContenu, apercu]);

    const identifier = useCallback(async (e) => {
        e.preventDefault();
        setEnCours(true);
        setErreurIdent(null);
        try {
            const r = await stagePublicAPI.identifier(code, identite);
            poserContenu(r.data);
        } catch (err) {
            // ⚠️ Le serveur renvoie 401 avec un message unique, quel que soit le
            // champ fautif. On l'affiche tel quel : c'est lui qui décide, pas
            // l'écran.
            setErreurIdent(err.response?.data?.error
                || "Identification impossible. Réessayez, ou contactez le secrétariat des stages.");
        } finally {
            setEnCours(false);
        }
    }, [code, identite, poserContenu]);

    const soumettre = useCallback(async (e) => {
        e.preventDefault();
        if (apercu) {
            setEnvoye(apercu.vue === 'maitre'
                ? "Aperçu : rien n'a été envoyé. Dans la vraie page, le maître de stage verrait ici la confirmation de sa validation."
                : "Aperçu : rien n'a été envoyé. Dans la vraie page, l'étudiant verrait ici la confirmation de son envoi.");
            return;
        }
        setEnCours(true);
        setErreurForm(null);
        try {
            // ⚠️ Le maître n'envoie AUCUNE identification (E56) : son jeton est
            // sa porte. L'étudiant en envoie une, différente selon sa porte.
            const r = porte === 'maitre'
                ? await stagePublicAPI.soumettreMaitre(token, { valeurs, sections })
                : porte === 'tokenisee'
                    ? await stagePublicAPI.soumettreTokenise(token, { numero: identite.numero, dateNaissance: identite.dateNaissance, valeurs, sections })
                    : await stagePublicAPI.soumettreCommune(code, { identification: identite, valeurs, sections });
            setEnvoye(r.data.message);
        } catch (err) {
            setErreurForm(err.response?.data?.error || "L'enregistrement a échoué. Réessayez.");
        } finally {
            setEnCours(false);
        }
    }, [apercu, porte, code, token, identite, valeurs, sections]);

    const poser = (cle, v) => setValeurs((x) => ({ ...x, [cle]: v }));
    const poserSection = (id, v) => setSections((x) => ({ ...x, [id]: v }));

    // ── Les états de page ───────────────────────────────────────────────────

    if (porte === 'depot' && enteteDepot) return <StageDepotPublic token={token} entete={enteteDepot} />;
    if (porte === 'certif_depot' && enteteDepot) return <StageCertifDepotPublic token={token} entete={enteteDepot} />;
    if (porte === 'certif_attestation' && enteteDepot) return <StageCertifMaitrePublic token={token} page={enteteDepot} />;
    if (porte === 'evaluation' && pageEval) return <StageEvaluationPublique token={token} page={pageEval} />;
    if (porte === 'visite' && pageEval) {
        // Lot 3.5c (E146) : le lien du tuteur ouvre la FICHE — attente avant le 1er jour du stage, puis saisie,
        // lecture et « Modifier la fiche ». Le jeton est la porte ; il n'est pas consommé.
        return (
            <StageVisitePublique page={pageEval}
                                 enregistrer={(corps) => stagePublicAPI.enregistrerVisite(token, corps)}
                                 recharger={() => stagePublicAPI.ouvrirLien(token).then((r) => r.data)} />
        );
    }

    if (chargement) {
        return (
            <div className="pub-page">
                <div className="pub-message">
                    <i className="fa-solid fa-circle-notch pub-tourne" />
                    <div>Ouverture du formulaire…</div>
                </div>
            </div>
        );
    }

    if (erreurPage) {
        return (
            <Message ton="erreur" icone="fa-link-slash" titre={erreurPage.titre}>
                <p>{erreurPage.detail}</p>
                {/* ⚠️ Chaque refus porte SON message : « expiré », « déjà envoyé »
                    et « remplacé » n'appellent pas la même réaction. Un « lien
                    invalide » unique laisserait le destinataire sans savoir s'il
                    doit chercher un autre courriel ou en redemander un. */}
            </Message>
        );
    }

    if (envoye) {
        return (
            <Message ton="succes" icone="fa-circle-check" titre="Fiche enregistrée">
                <p>{envoye}</p>
                <p className="pub-discret">Vous pouvez fermer cette page.</p>
            </Message>
        );
    }

    // ── Porte commune : l'identification ────────────────────────────────────
    if (!contenu) {
        return (
            <div className="pub-page">
                <div className="form-public">
                    <div className="form-public-header">
                        <h2><i className="fa-solid fa-building" /> Fiche de liaison — Stage {campagne?.formation_nom}</h2>
                        <p>
                            {campagne?.departement_nom}{campagne?.semestre ? ` · ${campagne.semestre}` : ''} · {campagne?.annee_libelle}
                            {campagne?.date_debut && ` — du ${dateFr(campagne.date_debut)} au ${dateFr(campagne.date_fin)}`}
                        </p>
                    </div>
                    <form className="form-public-body" onSubmit={identifier}>
                        <div className="form-section">
                            <h3><i className="fa-solid fa-id-card" /> Identifiez-vous</h3>
                            <p className="pub-aide pub-aide-bloc">
                                Ces quatre informations nous permettent de retrouver votre dossier.
                                Elles doivent correspondre à votre inscription.
                            </p>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="id-num">Numéro étudiant <span className="form-required">*</span></label>
                                    <input id="id-num" value={identite.numero} autoFocus
                                           placeholder="Ex : 20257566"
                                           onChange={(e) => setIdentite({ ...identite, numero: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="id-naiss">Date de naissance <span className="form-required">*</span></label>
                                    <ChampDate id="id-naiss" value={identite.dateNaissance}
                                           onChange={(e) => setIdentite({ ...identite, dateNaissance: e.target.value })} />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="id-nom">Nom <span className="form-required">*</span></label>
                                    <input id="id-nom" value={identite.nom}
                                           onChange={(e) => setIdentite({ ...identite, nom: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="id-prenom">Prénom <span className="form-required">*</span></label>
                                    <input id="id-prenom" value={identite.prenom}
                                           onChange={(e) => setIdentite({ ...identite, prenom: e.target.value })} />
                                </div>
                            </div>

                            {erreurIdent && (
                                <div className="pub-encart pub-encart-erreur">
                                    <i className="fa-solid fa-circle-exclamation" /> {erreurIdent}
                                </div>
                            )}
                        </div>
                        <div className="form-public-footer">
                            <button type="submit" className="pub-btn" disabled={enCours}>
                                {enCours
                                    ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Vérification…</>
                                    : <><i className="fa-solid fa-arrow-right" /> Accéder à ma fiche</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // ── Le formulaire ───────────────────────────────────────────────────────
    const d = contenu.dossier;
    const m = contenu.modele;
    const pourMaitre = porte === 'maitre';

    /**
     * ⚠️ LA CASE 🏛️ SORT DU LOT DES SECTIONS LIBRES.
     *
     * Noyée parmi d'« Informations complémentaires », elle serait la dernière
     * chose que le maître de stage lirait — alors que c'est la seule réponse que
     * le département attend vraiment de lui, et celle qui remonte en colonne
     * dans le tableau de bord. Elle a donc son encadré, avec son ancre 🏛️.
     */
    const sectionConvention = (contenu.sections || []).find((s) => s.code === CODE_CONVENTION);
    const sectionsLibres = (contenu.sections || []).filter((s) => s.code !== CODE_CONVENTION);

    return (
        <div className="pub-page">
            <form className="form-public" onSubmit={soumettre}>
                <div className="form-public-header">
                    <h2><i className="fa-solid fa-building" /> Fiche de liaison — Stage {d.formation_nom}</h2>
                    <p>
                        {pourMaitre ? 'À compléter par le maître de stage' : "À compléter par l'étudiant"}
                        {' — '}{d.departement_nom}
                    </p>
                </div>

                <div className="form-public-body">
                    {apercu && (
                        <div className="pub-encart pub-encart-info">
                            <i className="fa-solid fa-eye" /> Aperçu — données fictives. Cette page est celle que reçoit
                            {apercu.vue === 'maitre' ? ' le maître de stage' : " l'étudiant"} ; rien n'est envoyé.
                        </div>
                    )}
                    {m?.texte_intro && <p className="pub-intro">{m.texte_intro}</p>}
                    {/* L3b (E172) : la fiche d'un apprenti arrive pré-remplie depuis le registre de l'alternance. */}
                    {contenu?.prerempli === 'registre_alternance' && (
                        <p className="pub-intro" style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '8px 12px', color: '#5b21b6' }}>
                            <i className="fa-solid fa-handshake" /> Vous êtes apprenti : l'entreprise et le maître d'apprentissage sont repris du
                            registre de l'alternance. Vérifiez-les, complétez le reste, puis envoyez.
                        </p>
                    )}

                    {/* ⚠️ Le maître de stage arrive sur un formulaire DÉJÀ REMPLI.
                        Sans un mot pour le dire, il croirait devoir tout saisir —
                        ou pire, penserait que ces réponses sont les siennes et les
                        laisserait telles quelles sans les relire. */}
                    {pourMaitre && (
                        <div className="pub-encart pub-encart-info">
                            <i className="fa-solid fa-circle-info" />
                            <span>
                                Les informations ci-dessous ont été saisies par l'étudiant.
                                <strong> Merci de les vérifier et de les corriger si besoin</strong>, puis
                                de compléter la fin du formulaire.
                            </span>
                        </div>
                    )}

                    <div className="form-section">
                        <h3><i className="fa-solid fa-user-graduate" /> Étudiant concerné</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Nom et prénom</label>
                                <input value={`${d.nom} ${d.prenom}`} readOnly />
                            </div>
                            {/* ⚠️ Le numéro n'est JAMAIS envoyé par le serveur (E79). Sur le
                                lien commun, on montre ce que l'étudiant a lui-même tapé ;
                                sur le lien de relance et chez le maître, rien. */}
                            {porte === 'commune' && (
                                <div className="form-group">
                                    <label>Numéro étudiant</label>
                                    <input value={identite.numero} readOnly />
                                </div>
                            )}
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Formation</label>
                                <input value={d.semestre ? `${d.formation_nom} — ${d.semestre}` : d.formation_nom} readOnly />
                            </div>
                            <div className="form-group">
                                <label>Période de stage</label>
                                <input value={d.date_debut ? `${dateFr(d.date_debut)} → ${dateFr(d.date_fin)}` : '—'} readOnly />
                            </div>
                        </div>

                        {/* Porte tokenisée : le jeton prouve qu'on a reçu le message,
                            pas qu'on est le bon étudiant. Un courriel transféré au
                            camarade de TD suffirait sans ce contrôle. */}
                        {/* ⚠️ Et il ne vaut rien si la page affiche la réponse (E79). */}
                        {contenu.controle_identite && (<>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="ctl-num">
                                        Votre numéro étudiant <span className="form-required">*</span>
                                    </label>
                                    <input id="ctl-num" value={identite.numero} placeholder="Ex : 20257566"
                                           autoComplete="off"
                                           onChange={(e) => setIdentite({ ...identite, numero: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="ctl-naiss">
                                        Votre date de naissance <span className="form-required">*</span>
                                    </label>
                                    <ChampDate id="ctl-naiss" value={identite.dateNaissance}
                                           onChange={(e) => setIdentite({ ...identite, dateNaissance: e.target.value })} />
                                </div>
                            </div>
                            <span className="pub-aide">
                                Si vous avez reçu ce lien par erreur, ne complétez pas ce formulaire.
                            </span>
                        </>)}
                    </div>

                    <div className="form-section">
                        <h3><i className="fa-solid fa-building" /> Informations entreprise</h3>
                        <div className="form-row">
                            {CHAMPS_ENTREPRISE.map((c) => (
                                <Champ key={c.cle} def={c} valeur={valeurs[c.cle]} onChange={poser} disabled={enCours} />
                            ))}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3><i className="fa-solid fa-id-card" /> Maître de stage</h3>
                        <div className="form-row">
                            <ChoixCivilite valeur={valeurs.maitre_civilite} onChange={poser} disabled={enCours} pourMaitre={pourMaitre} />
                            {CHAMPS_MAITRE.map((c) => (
                                <Champ key={c.cle} def={c} valeur={valeurs[c.cle]} onChange={poser} disabled={enCours} />
                            ))}
                        </div>
                        <span className="pub-aide">
                            {pourMaitre
                                ? "C'est à cette adresse que le département vous écrira pour la convention de stage."
                                : 'C’est à cette adresse que la fiche sera envoyée pour validation : vérifiez-la.'}
                        </span>
                    </div>

                    {/* La section « missions » n'existe que si le modèle la demande :
                        un BUT1 est un stage de découverte, sans mission définie. */}
                    {m?.afficher_missions && (
                        <div className="form-section form-section-accent">
                            <h3><i className="fa-solid fa-list-check" /> Missions proposées</h3>
                            <div className="form-group form-group-large">
                                <label htmlFor="f-missions">
                                    {m.missions_libelle || "Décrivez les missions confiées à l'étudiant"}
                                </label>
                                <textarea id="f-missions" value={valeurs.missions || ''}
                                          placeholder={m.missions_placeholder || ''}
                                          disabled={enCours}
                                          onChange={(e) => poser('missions', e.target.value)} />
                            </div>
                        </div>
                    )}

                    {sectionsLibres.length > 0 && (
                        <div className="form-section">
                            <h3>
                                <i className="fa-solid fa-circle-plus" />
                                {pourMaitre ? ' À compléter par vos soins' : ' Informations complémentaires'}
                            </h3>
                            {sectionsLibres.map((s) => (
                                <SectionLibre key={s.id} section={s} valeur={sections[s.id]}
                                              onChange={poserSection} disabled={enCours} />
                            ))}
                        </div>
                    )}

                    {/* ── LA CONVENTION 🏛️ ─────────────────────────────────────
                        Son propre encadré, accentué comme la section « missions »
                        du modèle BUT2/3 : c'est la réponse que le département
                        attend, et celle qui remonte en colonne dans le tableau
                        de bord (écart E54). */}
                    {sectionConvention && (
                        <div className="form-section form-section-accent">
                            <h3><i className="fa-solid fa-landmark" /> Convention de stage</h3>
                            <SectionLibre section={sectionConvention} valeur={sections[sectionConvention.id]}
                                          onChange={poserSection} disabled={enCours} />
                        </div>
                    )}

                    {erreurForm && (
                        <div className="pub-encart pub-encart-erreur">
                            <i className="fa-solid fa-circle-exclamation" /> {erreurForm}
                        </div>
                    )}
                </div>

                <div className="form-public-footer">
                    <button type="submit" className="pub-btn" disabled={enCours}>
                        {enCours
                            ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Enregistrement…</>
                            : <>
                                <i className="fa-solid fa-check" />
                                {/* Le maître VALIDE la fiche de quelqu'un d'autre ;
                                    l'étudiant valide la sienne. Le libellé de la
                                    maquette pour la vue maître : « Valider la fiche
                                    de liaison ». */}
                                {pourMaitre ? ' Valider la fiche de liaison' : ' Valider ma fiche de liaison'}
                              </>}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default StageFicheLiaisonPublique;
